/*
 * Design tokens extracted verbatim from
 *   .mockup/current/birdsy-dashboard-v2-preview.html
 * (dark = the mockup's :root, light = its [data-theme="light"] override).
 *
 * Exposed on the MUI theme as `theme.custom` (see App.js). Additive: reading these does not
 * affect the global palette or any existing screen. New/restyled screens read from here.
 *
 * Priority order for the redesign is layout > font size > colour, so the `radius`, `fontSize`
 * and `fontWeight` scales matter as much as the colours below.
 */

// shared across both modes (the mockup does not redefine these in light mode)
const radius = { lg: 16, md: 12, sm: 8 }

const fontSize = {
	display: 20, // section / kit title (800)
	lg: 15,      // block name (700)
	md: 14.5,    // primary body
	base: 14,    // body
	sm: 13,      // muted / secondary
	xs: 12,      // captions
	xxs: 11,     // chip / label (700, often uppercase)
	micro: 10,   // smallest labels
}

const fontWeight = { bold: 800, semibold: 700, medium: 600 }

// tooltip is dark in both modes in the mockup (light does not override it)
const tooltip = { bg: '#1E293B', text: '#F8FAFC' }

export const darkTokens = {
	bg: {
		main: '#0F101A',
		surface: '#1A1C2E',
		cardInner: '#121324',
		hud: '#151726',
		dropdownHover: '#21243B',
		// hover for cards that sit ON the page bg: lifts in dark (shadow is invisible there),
		// stays white in light so the card pops off its shadow instead of graying out
		surfaceHover: '#21243B',
		// cards that sit ON a surface: lighter than it in dark, subtly darker in light
		raised: '#21243B',
		raisedHover: '#2A2E4C',
	},
	text: { main: '#FFFFFF', muted: '#8E92B2', subtopic: '#6E7299' },
	border: { color: '#262945', strong: '#34375C' },
	hud: { readiness: '#6366F1', streak: '#F59E0B', perf: '#06B6D4' },
	indigo: { primary: '#6366F1', border: '#818CF8', hover: '#4F46E5', hoverBorder: '#A5B4FC' },
	// gold is unchanged from amber in dark mode — amber reads as gold against a dark ground already,
	// and only light mode had the problem gold was added to fix (179)
	status: { green: '#34D399', greenStrong: '#10B981', amber: '#F59E0B', danger: '#F87171', lime: '#A3E635', gold: '#F59E0B', goldText: '#F59E0B' },
	chip: { bg: '#24273F', border: '#3B3F68', text: '#C7CAE8' },
	tutorBtn: { bg: '#21243B', border: '#4F5278' },
	tooltip,
	progressEmpty: '#262945',
	link: '#818CF8',
	dividerDot: '#4F5278',
	shadow: { card: '0 1px 2px rgba(0, 0, 0, 0.25)', pop: '0 12px 32px rgba(0, 0, 0, 0.35)' },
	radius,
	fontSize,
	fontWeight,
}

export const lightTokens = {
	bg: {
		main: '#FFFFFF',
		surface: '#FFFFFF',
		cardInner: '#F8FAFC',
		hud: '#F8FAFC',
		dropdownHover: '#EEF0F6',
		// hover for cards that sit ON the page bg: lifts in dark (shadow is invisible there),
		// stays white in light so the card pops off its shadow instead of graying out
		surfaceHover: '#FFFFFF',
		// cards that sit ON a surface: lighter than it in dark, subtly darker in light
		raised: '#F8FAFC',
		raisedHover: '#EEF0F6',
	},
	text: { main: '#0F172A', muted: '#5B6478', subtopic: '#6B7280' },
	border: { color: '#E4E7F0', strong: '#CBD1E0' },
	hud: { readiness: '#4F46E5', streak: '#D97706', perf: '#0891B2' },
	indigo: { primary: '#4F46E5', border: '#6366F1', hover: '#4338CA', hoverBorder: '#818CF8' },
	/*
	 * One rung darker than the 600s these were (120), so a score is legible as TEXT on a light
	 * background. Measured against both light surfaces — #FFFFFF and bg.raised #F8FAFC:
	 *
	 *              was            now
	 *   green      3.77:1 fail    5.48:1 pass   #059669 -> #047857
	 *   amber      3.19:1 fail    5.02:1 pass   #D97706 -> #B45309
	 *   danger     4.83:1 pass    6.47:1 pass   #DC2626 -> #B91C1C
	 *
	 * WCAG AA wants 4.5:1 for body text; amber and green were both short of it, so a score in
	 * either colour was hard to read on white and worse on a raised card. Dark mode already sat at
	 * 5.5-8.7:1 and is untouched — darkTokens is a separate object above.
	 *
	 * danger moves too even though it passed: with the other two darkened, a 600-weight red beside
	 * a 700-weight green and amber reads as a different family rather than one scale.
	 *
	 * Safe for the non-text uses as well. These also appear as bar fills, ring strokes and 8%
	 * tints, and a darker hue is equal or better against a light background in every one.
	 */
	/*
	 * lime is the readiness meter's "in progress" dash (171). It is a NON-TEXT indicator, so the bar
	 * is 3:1 rather than 4.5:1 — and that is what rules out the brighter chartreuse the mockup shows:
	 * lime-400 #A3E635 measures 1.6:1 on white and lime-500 #84CC16 measures 2.2:1, both invisible to
	 * anyone who needs contrast. lime-600 is 3.3:1 and passes. Dark mode keeps the bright one, where
	 * it has 11:1 against bg.hud.
	 */
	/*
	 * gold (179) is a DELIBERATE contrast exception, requested knowingly: #FFCC00 measures 1.51:1 on
	 * white against the 3:1 floor for non-text UI. amber #B45309 passes at 5.02:1 but reads brown at
	 * bar size, and the progress report is mostly bars.
	 *
	 * FOR FILLS AND BORDERS ONLY. goldText is the same band as TEXT: #8B6F00, 4.80:1 on white, the
	 * lightest gold that clears the 4.5:1 text minimum. It is darker but still GOLD — hue 48°, where
	 * amber #B45309 is hue 28°, which is why that one read as brown rather than merely dark.
	 *
	 * Dark mode needs no split: #F59E0B is 7.83:1 on bg.hud, so bar and text are the same value there.
	 */
	status: { green: '#047857', greenStrong: '#047857', amber: '#B45309', danger: '#B91C1C', lime: '#65A30D', gold: '#FFCC00', goldText: '#8B6F00' },
	chip: { bg: '#E8EBF7', border: '#C7CEEF', text: '#3730A3' },
	tutorBtn: { bg: '#F1F5F9', border: '#94A3B8' },
	tooltip,
	progressEmpty: '#E7EAF2',
	link: '#4F46E5',
	dividerDot: '#CBD5E1',
	shadow: { card: '0 1px 2px rgba(15, 23, 42, 0.06)', pop: '0 12px 32px rgba(15, 23, 42, 0.14)' },
	radius,
	fontSize,
	fontWeight,
}
